/**
 * Registering a patient who is not in the imported snapshot (DECISIONS.md #40).
 *
 * Mirrors `api/src/Continuum.Api/Patients/PatientEndpoints.cs`'s `POST /api/patients` and the
 * codes `PatientRegistration.Validate` and `PatientRegistrationService` return.
 *
 * **POST onto the collection, not PUT onto an id.** The client does not choose the `record_id`
 * and cannot: an id it picked would be an id it could pick twice, and there is no `DELETE` to
 * undo the second one with (AGENTS.md §3).
 *
 * **Not idempotent and not retryable on a timeout**, for the reason `noteApi.post` gives about
 * a note. There is no client-supplied key to deduplicate on, so a retry after an uncertain
 * outcome registers a second patient under the same reservnummer that no `DELETE` can remove.
 * What makes that survivable rather than merely documented is the server: the write takes an
 * advisory lock on the identifier and refuses a second one with
 * `REGISTRATION_IDENTIFIER_IN_USE`. The screen still never retries by itself.
 *
 * **A reservnummer only.** The form cannot represent a personnummer — see `RegisteredIdentifier`
 * on the server — because phase 0 must never hold a real one (AGENTS.md §6) and a patient who
 * *has* a personnummer is already in the snapshot and is reached by searching for them.
 */

import { unauthenticated } from '../auth/sessionExpiry'

/**
 * What the form sends. Mirrors `PatientRegistrationRequest`.
 *
 * Every optional field is `null` rather than omitted, so that "the clinician left this empty" is
 * one thing on the wire instead of two. **Nothing is defaulted on the clinician's behalf** — a
 * sex of `unknown` the interface chose reads in the record exactly like one somebody established.
 */
export interface RegistrationSubmission {
  /** The reservnummer, typed off a pre-printed band. */
  readonly identifier: string

  /** The unit that owns the block the number came from. A block is only unique if it has one. */
  readonly assigner: string

  readonly givenName: string | null
  readonly familyName: string | null

  /** ISO `YYYY-MM-DD`, or null when the birth date is not known. */
  readonly birthDate: string | null

  /** HL7 administrative gender as a code, or null. `unknown` is a real answer, not an absence. */
  readonly sex: string | null

  /**
   * True when the identity is a working assumption rather than a claim — the unidentified
   * patient (AGENTS.md §6). Required when there is no family name; the server refuses otherwise.
   */
  readonly isEstimatedIdentity: boolean

  /** The ward's `code`, e.g. `M3`. */
  readonly ward: string

  /** Bed as staff say it, or null — a patient can be in a corridor. */
  readonly bed: string | null
}

/** What a successful registration returns. */
export interface RegisteredPatient {
  /** The new patient's `record_id`, which is what opens their overview. */
  readonly patientId: string

  /** The ward code they were admitted to, echoed back from the row that was written. */
  readonly ward: string
}

/**
 * Every code a registration can be refused with.
 *
 * Listed rather than left as `string` so that a code added on the server without wording here is
 * a typecheck failure and not a blank message on a screen where a clinician is holding a number
 * off a wristband. `registrationRefusalMessage` is exhaustive over this by construction.
 *
 * `WARD_NOT_FOUND` and `ROLE_MAY_NOT_DOCUMENT` are in the list and are not `REGISTRATION_`
 * codes: the first is a fact about the record and the second comes from `AccessGate`, shared
 * with the note write. Both arrive here, so both need wording.
 */
export const REGISTRATION_REFUSAL_CODES = [
  'REGISTRATION_BODY_MISSING',
  'REGISTRATION_IDENTIFIER_MISSING',
  'REGISTRATION_IDENTIFIER_IS_NATIONAL_NUMBER',
  'REGISTRATION_IDENTIFIER_MALFORMED',
  'REGISTRATION_IDENTIFIER_IN_USE',
  'REGISTRATION_ASSIGNER_MISSING',
  'REGISTRATION_FAMILY_NAME_MISSING',
  'REGISTRATION_FIELD_TOO_LONG',
  'REGISTRATION_SEX_UNKNOWN',
  'REGISTRATION_BIRTH_DATE_MALFORMED',
  'REGISTRATION_BIRTH_DATE_IN_FUTURE',
  'REGISTRATION_WARD_MISSING',
  'WARD_NOT_FOUND',
  'ROLE_MAY_NOT_DOCUMENT',
] as const

export type RegistrationRefusalCode = (typeof REGISTRATION_REFUSAL_CODES)[number]

/** Params a refusal can carry. Which are present depends on the code. */
export interface RegistrationRefusalParams {
  /** `REGISTRATION_IDENTIFIER_MALFORMED`: the shape, so the catalogue holds no second copy. */
  readonly prefixLength?: number
  readonly sequenceLength?: number

  /** `REGISTRATION_IDENTIFIER_IN_USE`: the number that is already filed. */
  readonly identifier?: string

  /** `REGISTRATION_FIELD_TOO_LONG`: which field, by its submission name, and the limit. */
  readonly field?: string
  readonly max?: number

  /** `REGISTRATION_SEX_UNKNOWN`: what was sent. */
  readonly sex?: string

  /** The two birth-date codes: what was typed, quoted back. */
  readonly birthDate?: string

  /** `WARD_NOT_FOUND`: the code that did not resolve. */
  readonly ward?: string

  /** `ROLE_MAY_NOT_DOCUMENT`: the role that was refused. */
  readonly roleCode?: string
}

/**
 * The server refused the registration, with the code it refused by.
 *
 * Distinct from {@link RegistrationUnavailableError} because the two mean opposite things to a
 * clinician with a patient in front of them: a refusal is something to fix in the form, and an
 * unavailable API is something to wait out **without closing the form** — the typed details are
 * nowhere else.
 */
export class RegistrationRefusedError extends Error {
  readonly code: RegistrationRefusalCode | 'UNKNOWN'
  readonly params: RegistrationRefusalParams
  readonly status: number

  constructor(
    code: RegistrationRefusalCode | 'UNKNOWN',
    params: RegistrationRefusalParams,
    status: number,
  ) {
    super(`Registration refused: ${code}`)
    this.name = 'RegistrationRefusedError'
    this.code = code
    this.params = params
    this.status = status
  }
}

/** The API could not be reached, or answered something unusable. */
export class RegistrationUnavailableError extends Error {
  constructor() {
    super('Registration unavailable')
    this.name = 'RegistrationUnavailableError'
  }
}

function isRefusalCode(value: unknown): value is RegistrationRefusalCode {
  return (
    typeof value === 'string' &&
    (REGISTRATION_REFUSAL_CODES as readonly string[]).includes(value)
  )
}

/**
 * Turns a non-2xx response into the error to throw.
 *
 * An unreadable or unrecognised body still becomes a {@link RegistrationRefusedError} with code
 * `UNKNOWN` rather than an unavailable error, because the server did answer: the patient was not
 * registered, and telling the clinician to wait would be wrong.
 */
async function refusalFrom(response: Response): Promise<RegistrationRefusedError> {
  try {
    const body = (await response.json()) as {
      error?: { code?: unknown; params?: RegistrationRefusalParams }
    }

    return new RegistrationRefusedError(
      isRefusalCode(body.error?.code) ? body.error.code : 'UNKNOWN',
      body.error?.params ?? {},
      response.status,
    )
  } catch {
    return new RegistrationRefusedError('UNKNOWN', {}, response.status)
  }
}

/**
 * Registers a patient: one `clinical.patient`, one `clinical.patient_identifier` and one
 * `clinical.encounter`, in one transaction on the server.
 *
 * **What this writes cannot be removed** (AGENTS.md §3). An incorrect registration is corrected by
 * inserting a new version — `correctPatient`, for the demographics — and the reservnummer is not
 * among the things a correction can change. So the form checks what it can before sending, and the
 * server checks all of it again.
 */
export async function registerPatient(
  submission: RegistrationSubmission,
  signal: AbortSignal,
): Promise<RegisteredPatient> {
  let response: Response

  try {
    response = await fetch('/api/patients', {
      method: 'POST',
      signal,
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(submission),
    })
  } catch (cause) {
    // An abort is the clinician having navigated away mid-request. Re-thrown so the caller can
    // tell it from a failure — but a write that reached the server still happened, which is why
    // the form does not abort a submit in flight. See `PatientRegistrationScreen`.
    if (signal.aborted) {
      throw cause
    }
    throw new RegistrationUnavailableError()
  }

  // Before `refusalFrom`, which reads the body, and before it for the reason as much as the
  // order: a 401 is not a refusal of this registration, it is the session having ended, and
  // `SessionGate` is about to show the login screen. Reported rather than rendered — see
  // `auth/sessionExpiry.ts`.
  unauthenticated(response)

  if (!response.ok) {
    throw await refusalFrom(response)
  }

  try {
    return (await response.json()) as RegisteredPatient
  } catch {
    // A 201 whose body did not parse is the worst of the failure modes: the patient is in the
    // record and this client cannot say who. Reported as unavailable so the success path is not
    // taken — the clinician searches for the number and finds them, rather than being told the
    // registration failed and registering a second copy.
    throw new RegistrationUnavailableError()
  }
}
